import { Box, Button, Typography, useTheme } from '@mui/material';
import log from 'loglevel';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useRouteMatch } from 'react-router';
import { RedirectUrlFrom, RedirectUrlType } from '../../Common/models/RedirectUrlModels';
import { CommonActions } from '../../Common/redux/CommonActions';
import * as ROUTES from '../../config/routes';
import { Order } from '../../my-lemonade-library/model/Order';
import { EnvNames } from '../../my-lemonade-library/src/common/configs/EnvNames';
import LyfPaymentIntentInfos from '../../my-lemonade-library/src/payments/models/lyf/LyfPaymentIntentInfos';
import { RootState, useTypedSelector } from '../../redux/root-reducer';

interface PaymentLyfEmbeddedProps {
    order: Order;
    paymentInfos: LyfPaymentIntentInfos;
    returnHomePage: () => void;
}

const PaymentLyfEmbedded: React.FC<PaymentLyfEmbeddedProps> = ({
    order,
    paymentInfos,
    returnHomePage
}) => {
    const { sessionId } = useTypedSelector((state: RootState) => state.locations);

    const intl = useIntl();
    const theme = useTheme();
    const dispatch = useDispatch();
    const match = useRouteMatch<ROUTES.RouteParams>();

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sdkLoaded, setSdkLoaded] = useState(false);
    const initializationRef = useRef(false);

    // Clean up Stripe elements on mount
    useEffect(() => {
        if (window.Stripe) {
            delete (window as any).Stripe;
        }
        document.querySelectorAll('script[src*="stripe.com"], iframe[src*="stripe.com"]').forEach(el => {
            el.remove();
        });
    }, []);

    const loadLyfSDK = useCallback((callback: () => void) => {
        if ('LyfCheckout' in window) {
            setSdkLoaded(true);
            callback();
            return;
        }

        const isDevEnv = process.env.REACT_APP_ENV === EnvNames.DEV;
        const lyfSdkUrl = isDevEnv
            ? 'https://sandbox-pay.lyf.eu/sdk/lyf-checkout.js'
            : 'https://pay.lyf.eu/sdk/lyf-checkout.js';

        const script = document.createElement('script');
        script.src = lyfSdkUrl;
        script.async = true;
        script.onload = () => {
            setSdkLoaded(true);
            callback();
        };
        script.onerror = () => {
            setError("Error while loading Lyf SDK");
            setIsLoading(false);
        };
        document.head.appendChild(script);
    }, []);

    useEffect(() => {
        loadLyfSDK(() => {
            // SDK loaded
        });
    }, [loadLyfSDK]);

    const paymentConfig = useMemo(() => {
        const successUrl = paymentInfos?.order_payment.success_url ??
            ROUTES.getOrderConfirmationFullRoute(
                match.params.tableLinkKey,
                order.id,
                undefined,
                sessionId
            );

        return {
            useCallbacks: true,
            onSuccess: () => {
                log.info(`Payment success for order ${order.id}`);
                dispatch(CommonActions.setRedirectURL(
                    successUrl,
                    undefined,
                    RedirectUrlType.CONFIRM,
                    RedirectUrlFrom.PAYMENT_LYF
                ));
            },
            onError: () => {
                log.warn(`Payment error for order ${order.id}`);
                setError("An error occurred during payment");
            },
            onCancel: returnHomePage
        };
    }, [paymentInfos, order, match.params.tableLinkKey, sessionId, dispatch, returnHomePage]);

    useEffect(() => {
        if (!sdkLoaded || initializationRef.current) {
            return;
        }

        const intentId = paymentInfos.intentId;
        const container = document.getElementById('lyf-payment-container');

        if (!container) {
            setError("Payment container not found");
            setIsLoading(false);
            return;
        }

        try {
            const LyfCheckout = (window as any).LyfCheckout;
            const lc = new LyfCheckout(intentId, paymentConfig);
            lc.open(container);
            initializationRef.current = true;
            setIsLoading(false);
        } catch (err) {
            setError(`Error initializing Lyf form: ${err}`);
            setIsLoading(false);
        }
    }, [sdkLoaded, paymentInfos, paymentConfig]);

    if (error) {
        return (
            <Box
                py={3}
                px={5}
                width="100%"
                height="100%"
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                alignItems="center"
            >
                <Box
                    flex={1}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                >
                    <Typography variant="h2" color="textSecondary">
                        {error}
                    </Typography>
                </Box>

                <Button
                    variant="contained"
                    color="primary"
                    style={{
                        marginTop: theme.spacing(3),
                        textTransform: "none",
                    }}
                    onClick={returnHomePage}
                >
                    <Typography>
                        {intl.formatMessage({ id: "returnHomePage" }, { br: <br /> })}
                    </Typography>
                </Button>
            </Box>
        );
    }

    return (
        <Box
            py={3}
            px={5}
            width="100%"
            height="100%"
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            alignItems="center"
        >
            <Box
                id="lyf-payment-container"
                width="100%"
                flex={1}
                minHeight="300px"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                style={{
                    border: '1px solid transparent'
                }}
            />

            {isLoading && (
                <Box
                    flex={1}
                    width="100%"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                >
                    <Typography variant="h2" color="textSecondary">
                        {intl.formatMessage({ id: "loading" })}
                    </Typography>
                </Box>
            )}

            <Button
                variant="contained"
                color="primary"
                style={{
                    marginTop: theme.spacing(3),
                    textTransform: "none",
                }}
                onClick={returnHomePage}
            >
                <Typography>
                    {intl.formatMessage({ id: "returnHomePage" }, { br: <br /> })}
                </Typography>
            </Button>
        </Box>
    );
};

export default PaymentLyfEmbedded;